import { useGet, useMutationWithErrorHandling, usePost, useQueryWithErrorHandling } from '@app/sdk';
import { AccountInterestRate, RepriceAccountRequest } from '../../models/interest';
import { BillingPeriodEstimatedInterest } from '../../models/ledger';

function useInterestService() {
  const get = useGet(true, "ams");
  const post = usePost(true, "ams");

  function getInterestSet(interestSetId) {
    return get(`/api/proxy/ams/interest-service/interest-set/${interestSetId}`);
  }

  function getAccountInterestRates(accountId: string) {
    return get<AccountInterestRate>(`/api/proxy/ams/interest-service/account/${accountId}/rates`);
  }

  function getAccountEffectiveRates(accountId: string) {
    return get<any>(`/api/proxy/ams/interest-service/account/${accountId}/effective-rates`);
  }

  function getAccountRateHistory(accountId: string) {
    return get(`/api/proxy/ams/interest-service/account/${accountId}/rate-history`);
  }

  function repriceAccount(accountId: string, request: RepriceAccountRequest) {
    return post(`/api/proxy/ams/interest-service/account/${accountId}/reprice`, request);
  }

  function getBillingPeriodEstimatedInterest(accountId: string, billingPeriodId: number) {
    return get<BillingPeriodEstimatedInterest>(
      `/api/proxy/ams/interest-service/account/${accountId}/estimated-interest/${billingPeriodId}`
    );
  }

  function reverseTransaction(accountId: string, transactionId: string, description: string) {
    const body = {
      description,
    };
    return post(
      `/api/proxy/ams/interest-service/account/${accountId}/interest-transaction/${transactionId}/reverse`,
      body
    );
  }

  function createInterestTransaction(accountId: string, amount: number, description: string, subledgerId: string) {
    const body = {
      description,
      allocations: [
        {
          subledgerId,
          amount: {
            value: amount,
            currencyCode: 'GBP',
          },
        },
      ],
    };
    return post(`/api/proxy/ams/interest-service/account/${accountId}/interest-transaction`, body);
  }

  function getAccruedInterestForBillingPeriod(accountId: string, billingPeriodId?: number) {
    if(billingPeriodId) return get<any>(`/api/proxy/ams/interest-service/account/${accountId}/accrued-interest?billingPeriodId=${billingPeriodId}`);
    return get<any>(`/api/proxy/ams/interest-service/account/${accountId}/accrued-interest`);
  }

  function getInterestAssessmentForBillingPeriod(accountId: string, billingPeriodId: number) {
    return get<any>(
      `/api/proxy/ams/interest-service/account/${accountId}/interest-assessment?billingPeriodId=${billingPeriodId}`
    );
  }

  return {
    getInterestSet,
    getAccountInterestRates,
    getAccountEffectiveRates,
    getAccountRateHistory,
    repriceAccount,
    getBillingPeriodEstimatedInterest,
    reverseTransaction,
    createInterestTransaction,
    getAccruedInterestForBillingPeriod,
    getInterestAssessmentForBillingPeriod,
  };
}

export function useRepriceAccount() {
  const { repriceAccount } = useInterestService();
  return useMutationWithErrorHandling({
    mutationFn: ({ accountId, request }: { accountId: string; request: RepriceAccountRequest }) =>
      repriceAccount(accountId, request),
  });
}

export function useGetAccountInterestRates({ accountId }: { accountId: string }) {
  const { getAccountInterestRates } = useInterestService();
  return useQueryWithErrorHandling({
    queryKey: ['accountInterestRates', accountId],
    queryFn: () => getAccountInterestRates(accountId),
    enabled: !!accountId,
    silent404: true,
  });
}

export function useGetAccountEffectiveRates({ accountId }: { accountId: string }) {
  const { getAccountEffectiveRates } = useInterestService();
  return useQueryWithErrorHandling({
    queryKey: ['accountEffectiveRates', accountId],
    queryFn: () => getAccountEffectiveRates(accountId),
    enabled: !!accountId,
    silent404: true,
  });
}

export function useGetBillingPeriodEstimatedInterest({
  accountId,
  billingPeriodId,
}: {
  accountId: string;
  billingPeriodId: number;
}) {
  const { getBillingPeriodEstimatedInterest } = useInterestService();
  return useQueryWithErrorHandling({
    queryKey: ['billingPeriodEstimatedInterest', accountId, billingPeriodId],
    queryFn: () => getBillingPeriodEstimatedInterest(accountId, billingPeriodId),
    enabled: !!accountId && !!billingPeriodId,
  });
}

export function useGetAccruedInterestForBillingPeriod({ accountId, billingPeriodId }: { accountId: string, billingPeriodId?: number }) {
  const { getAccruedInterestForBillingPeriod } = useInterestService();
  return useQueryWithErrorHandling({
    queryKey: ['accruedInterestForBillingPeriod', accountId],
    queryFn: () => getAccruedInterestForBillingPeriod(accountId, billingPeriodId),
    enabled: !!accountId,
    silent404: true,
  });
}

export function useGetInterestAssessmentForBillingPeriod({
  accountId,
  billingPeriodId,
}: {
  accountId: string;
  billingPeriodId: number;
}) {
  const { getInterestAssessmentForBillingPeriod } = useInterestService();
  return useQueryWithErrorHandling({
    queryKey: ['interestAssessmentForBillingPeriod', accountId, billingPeriodId],
    queryFn: () => getInterestAssessmentForBillingPeriod(accountId, billingPeriodId),
    enabled: !!accountId && !!billingPeriodId,
  });
}
